import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { UserCog, Users, GraduationCap } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RoleSelection = () => {
  const { sessionToken, updateUser } = useAuth();
  const navigate = useNavigate();
  const [selectedRole, setSelectedRole] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [loading, setLoading] = useState(false);

  const roles = [
    { id: 'admin', name: 'Admin', icon: UserCog, description: 'Manage entire school system' },
    { id: 'teacher', name: 'Teacher', icon: GraduationCap, description: 'Mark attendance and view reports' },
    { id: 'parent', name: 'Parent', icon: Users, description: 'View child attendance' }
  ];

  const handleRoleSubmit = async () => {
    if (!selectedRole) {
      toast.error('Please select a role');
      return;
    }

    if (selectedRole === 'parent' && !phoneNumber) {
      toast.error('Phone number is required for parents');
      return;
    }

    setLoading(true);
    try {
      await axios.put(
        `${API}/auth/role`,
        { role: selectedRole, phone_number: phoneNumber },
        { headers: { Authorization: `Bearer ${sessionToken}` } }
      );

      // Get updated user data
      const userResponse = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      updateUser(userResponse.data);

      toast.success('Role updated successfully!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Role update failed:', error);
      toast.error('Failed to update role. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-sky-50 to-white p-4">
      <Card className="w-full max-w-2xl p-8 rounded-2xl shadow-lg border-slate-100 bg-white" data-testid="role-selection-card">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2 font-heading">Select Your Role</h1>
          <p className="text-slate-500">Choose how you'll use the system</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {roles.map((role) => {
            const Icon = role.icon;
            return (
              <button
                key={role.id}
                onClick={() => setSelectedRole(role.id)}
                className={`p-6 rounded-2xl border-2 transition-all hover:shadow-md ${
                  selectedRole === role.id
                    ? 'border-sky-500 bg-sky-50 shadow-md'
                    : 'border-slate-200 hover:border-sky-200'
                }`}
                data-testid={`role-${role.id}-btn`}
              >
                <Icon className={`w-12 h-12 mx-auto mb-3 ${
                  selectedRole === role.id ? 'text-sky-500' : 'text-slate-400'
                }`} />
                <h3 className="font-semibold text-lg mb-1">{role.name}</h3>
                <p className="text-sm text-slate-500">{role.description}</p>
              </button>
            );
          })}
        </div>

        {selectedRole === 'parent' && (
          <div className="mb-6">
            <Label htmlFor="phone" className="text-slate-700 mb-2 block">Phone Number</Label>
            <Input
              id="phone"
              type="tel"
              placeholder="Enter your phone number"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              className="rounded-xl"
              data-testid="phone-input"
            />
            <p className="text-sm text-slate-500 mt-2">This will be used to link you with your children</p>
          </div>
        )}

        <Button
          onClick={handleRoleSubmit}
          disabled={loading || !selectedRole}
          className="w-full bg-sky-500 hover:bg-sky-600 text-white rounded-full py-6 text-lg font-semibold shadow-sm transition-all hover:shadow-md active:scale-95"
          data-testid="submit-role-btn"
        >
          {loading ? 'Updating...' : 'Continue to Dashboard'}
        </Button>
      </Card>
    </div>
  );
};

export default RoleSelection;